import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Search, SlidersHorizontal, ChevronDown, Sparkles, X, Loader2 } from "lucide-react";
import { listPublishedCases } from "../services/cases";
import { useAuth } from "../context/AuthContext";
import { matchCasesForDesigner, designerToMeta } from "../services/aiMatch";
import { CaseCard } from "../components/CaseCard";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

const RESTORATIONS = ["crown", "bridge", "veneer", "inlay", "implant", "denture", "other"];
const MATERIALS = ["zirconia", "emax", "pmma", "titanium", "metal", "other"];
const COMPLEXITIES = ["Low", "Medium", "High"];

const MP_STATE_KEY = "dcc_marketplace_state";

function loadMpState() {
  try {
    return JSON.parse(sessionStorage.getItem(MP_STATE_KEY)) || {};
  } catch {
    return {};
  }
}

export default function Marketplace() {
  const { t, i18n } = useTranslation();
  const { profile } = useAuth();
  const isDesigner = profile?.role === "designer";
  const saved = loadMpState();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState(saved.q || "");
  const [restoration, setRestoration] = useState(saved.restoration || "all");
  const [material, setMaterial] = useState(saved.material || "all");
  const [complexity, setComplexity] = useState(saved.complexity || "all");
  const [showFilters, setShowFilters] = useState(saved.filtersOpen || false);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiActive, setAiActive] = useState(false);
  const [aiMatches, setAiMatches] = useState({});
  const [aiIntro, setAiIntro] = useState(false);

  useEffect(() => {
    // Demo/test cases are excluded at the data layer (services/cases.js), so the
    // public Marketplace only ever shows real cases. Demo cases remain in the DB
    // and stay visible in Admin -> Cases -> Include Demo/Test.
    listPublishedCases()
      .then((list) => setCases(list))
      .catch(() => setCases([]))
      .finally(() => setLoading(false));
  }, []);

  // Persist filters/search so they survive navigating into a case and back.
  useEffect(() => {
    sessionStorage.setItem(MP_STATE_KEY, JSON.stringify({ q, restoration, material, complexity, filtersOpen: showFilters, scrollY: window.scrollY }));
  }, [q, restoration, material, complexity, showFilters]);

  // Restore scroll position once the list has rendered.
  useEffect(() => {
    if (loading) return;
    const y = loadMpState().scrollY;
    if (y) requestAnimationFrame(() => window.scrollTo(0, y));
  }, [loading]);

  // Capture the latest scroll offset before leaving the page.
  useEffect(() => {
    const onScroll = () => {
      const s = loadMpState();
      sessionStorage.setItem(MP_STATE_KEY, JSON.stringify({ ...s, scrollY: window.scrollY }));
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const filtered = useMemo(() => {
    return cases.filter((c) => {
      if (restoration !== "all" && c.restorationType !== restoration) return false;
      if (material !== "all" && c.material !== material) return false;
      if (complexity !== "all" && c.ai?.complexity !== complexity) return false;
      if (q.trim()) {
        const hay = `${c.title} ${(c.ai?.tags || []).join(" ")} ${c.toothNumbers}`.toLowerCase();
        if (!hay.includes(q.toLowerCase())) return false;
      }
      return true;
    });
  }, [cases, q, restoration, material, complexity]);

  const activeFilterCount = [restoration, material, complexity].filter((v) => v !== "all").length;

  // AI Match ranks the CURRENTLY visible cases against the designer's profile.
  const runAiMatch = async () => {
    if (aiBusy) return;
    setAiIntro(false);
    if (!filtered.length) { toast.info(t("aiMatch.noCases")); return; }
    setAiBusy(true);
    try {
      const meta = designerToMeta(profile?.designerProfile || {});
      const map = await matchCasesForDesigner(meta, filtered.slice(0, 40), i18n.language?.split("-")[0] || "en");
      setAiMatches(map);
      setAiActive(true);
    } catch {
      toast.error(t("aiMatch.unavailable"));
    } finally {
      setAiBusy(false);
    }
  };
  const clearAiMatch = () => { setAiActive(false); setAiMatches({}); };

  // When AI Match is active, order the visible cases by match score (desc).
  const displayed = useMemo(() => {
    if (!aiActive) return filtered;
    return [...filtered].sort((a, b) => ((aiMatches[b.id]?.score ?? -1) - (aiMatches[a.id]?.score ?? -1)));
  }, [filtered, aiActive, aiMatches]);

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
      <h1 className="font-heading text-3xl font-bold tracking-tight">{t("marketplace.title")}</h1>
      <p className="mt-1 text-muted-foreground">{t("marketplace.subtitle")}</p>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <Button
          variant="outline"
          className="rounded-full"
          onClick={() => setShowFilters((v) => !v)}
          data-testid="filters-toggle"
        >
          <SlidersHorizontal className="mr-1.5 h-4 w-4" />
          {t("common.filter")}
          {activeFilterCount > 0 && (
            <span className="ml-1.5 rounded-full bg-brand px-1.5 text-[11px] font-semibold text-white" data-testid="active-filter-count">{activeFilterCount}</span>
          )}
          <ChevronDown className={`ml-1.5 h-4 w-4 transition-transform ${showFilters ? "rotate-180" : ""}`} />
        </Button>
        {isDesigner && (
          aiActive ? (
            <Button variant="outline" className="rounded-full border-brand text-brand hover:bg-brand/10" onClick={clearAiMatch} data-testid="ai-match-clear">
              <X className="mr-1.5 h-4 w-4" /> {t("aiMatch.exit")}
            </Button>
          ) : (
            <Button className="rounded-full bg-gradient-to-r from-violet-600 to-brand font-semibold text-white hover:opacity-90" onClick={() => setAiIntro(true)} disabled={aiBusy} data-testid="ai-match-button">
              {aiBusy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Sparkles className="mr-1.5 h-4 w-4" />}
              {aiBusy ? t("aiMatch.finding") : t("aiMatch.cta")}
            </Button>
          )
        )}
      </div>

      <Dialog open={aiIntro} onOpenChange={setAiIntro}>
        <DialogContent data-testid="ai-match-intro-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-brand" /> {t("aiMatch.cta")}</DialogTitle>
            <DialogDescription className="pt-1 text-left leading-relaxed">{t("aiMatch.introDesigner")}</DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" className="rounded-full" onClick={() => setAiIntro(false)} data-testid="ai-match-intro-cancel">{t("common.cancel")}</Button>
            <Button className="rounded-full bg-gradient-to-r from-violet-600 to-brand font-semibold text-white hover:opacity-90" onClick={runAiMatch} data-testid="ai-match-intro-confirm">
              <Sparkles className="mr-1.5 h-4 w-4" /> {t("aiMatch.runDesigner")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {isDesigner && aiActive && (
        <div className="mt-3 flex items-start gap-2 rounded-lg border border-brand/30 bg-brand/5 px-4 py-2.5 text-xs text-muted-foreground" data-testid="ai-match-active-banner">
          <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-brand" />
          <span>{t("aiMatch.rankedNote")} <strong className="font-medium text-foreground">{t("aiMatch.disclaimer")}</strong></span>
        </div>
      )}

      <div className={`mt-4 grid gap-8 ${showFilters ? "lg:grid-cols-4" : "grid-cols-1"}`}>
        {/* Filters */}
        {showFilters && (
        <aside className="space-y-5 lg:col-span-1" data-testid="marketplace-filters">
          <div className="rounded-lg border border-border bg-card p-5">
            <p className="mb-4 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.1em] text-muted-foreground">
              <SlidersHorizontal className="h-3.5 w-3.5" /> {t("common.filter")}
            </p>
            <div className="space-y-4">
              <div>
                <p className="mb-1.5 text-xs font-medium text-muted-foreground">{t("marketplace.filterRestoration")}</p>
                <Select value={restoration} onValueChange={setRestoration}>
                  <SelectTrigger data-testid="filter-restoration"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t("common.all")}</SelectItem>
                    {RESTORATIONS.map((r) => <SelectItem key={r} value={r}>{t(`case.restorationTypes.${r}`)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <p className="mb-1.5 text-xs font-medium text-muted-foreground">{t("marketplace.filterMaterial")}</p>
                <Select value={material} onValueChange={setMaterial}>
                  <SelectTrigger data-testid="filter-material"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t("common.all")}</SelectItem>
                    {MATERIALS.map((m) => <SelectItem key={m} value={m}>{t(`case.materials.${m}`)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <p className="mb-1.5 text-xs font-medium text-muted-foreground">{t("marketplace.filterComplexity")}</p>
                <Select value={complexity} onValueChange={setComplexity}>
                  <SelectTrigger data-testid="filter-complexity"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t("common.all")}</SelectItem>
                    {COMPLEXITIES.map((c) => <SelectItem key={c} value={c}>{t(`complexity.${c}`)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </aside>
        )}

        {/* Listings */}
        <div className={showFilters ? "lg:col-span-3" : ""}>
          <div className="relative mb-5">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input className="pl-9" placeholder={t("marketplace.searchPlaceholder")} value={q} onChange={(e) => setQ(e.target.value)} data-testid="marketplace-search" />
          </div>
          <p className="mb-4 text-sm text-muted-foreground">{filtered.length} {t("marketplace.available")}</p>

          {loading ? (
            <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">{[1, 2, 3, 4, 5, 6].map((i) => <Skeleton key={i} className="h-72 rounded-lg" />)}</div>
          ) : filtered.length === 0 ? (
            <div className="rounded-lg border border-dashed border-border p-12 text-center text-muted-foreground" data-testid="no-results">{t("marketplace.noResults")}</div>
          ) : (
            <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
              {displayed.map((c, i) => <CaseCard key={c.id} caseItem={c} index={i} netForDesigner aiMatch={aiActive ? (aiMatches[c.id] || null) : null} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
